import { styled } from '@mui/system'
import { RectangleButton } from 'components/buttons'
import { GroupIcon } from 'icons'
import { Logo } from 'pages/logo/Logo'
import { LogoContainer } from 'pages/logoContainer/LogoContainer'
import { useFeature, useGrowthBook } from '@growthbook/growthbook-react'
import { useNavigateBackDisabled } from 'pages/useNavigateBackDisabled'
import { useEffect } from 'react'
import { pushCameraUsageToGA } from 'utils/get-camera-duration'
import { parseUnitNumber } from 'utils/level-unit-helpers'
import { ONLINE_ENCOUNTER, ActivityType } from 'api/classroom/ClassroomState'
import { StudentThankYouScreen } from './StudentThankYouScreen'
import { LevelCompletionScreen } from './levelCompletion/LevelCompletionScreen'
import { useIsContinueEncounterResult } from './levelCompletion/useIsContinueEncounterResult'
import {
    LEVEL_COMPLETION_FEATURE_FLAG,
    LevelCompletionVariant,
    NEXT_STEP_URL,
    SCREEN_VARIANT_QUERY_PARAM,
    UNIT_CODE_STORAGE_KEY,
    getLevelCompletionVariantLabel,
    parseLevelCompletionVariant
} from 'utils/constants'

const InnerWrapper = styled('div')(() => ({
    textAlign: 'center'
}))

const ThanksMessageWrapper = styled('div')(() => ({
    textAlign: 'center',
    marginTop: 45,
    marginBottom: 40
}))

const Title = styled('div')(({ theme }) => ({
    fontWeight: 800,
    fontSize: theme.spacing(6),
    color: theme.palette.primary?.main,
    lineHeight: theme.spacing(8),
    letterSpacing: '-0.01em'
}))

const Message = styled('div')<{ isTeacher?: boolean }>(({ theme, isTeacher }) => ({
    fontWeight: 400,
    fontSize: 18,
    color: 'rgba(0, 0, 0, 0.65)',
    paddingTop: theme.spacing(2),
    width: isTeacher ? '395px' : '100%',
    letterSpacing: '0.02em',
    lineHeight: '130%'
}))

/**
 * Determines if student feedback flow should be shown
 * Based on role and class type
 */
const shouldShowStudentFeedback = (): boolean => {

    const isTeacher = sessionStorage.getItem('role') === 'teacher'
    const isPostClassFeedbackEnabled = window?.growthbook?.isOn('student-post-class-feedback')

    if (isTeacher || !isPostClassFeedbackEnabled) return false

    // Show only for specific class types
    const activityType = sessionStorage.getItem('activityType') as ActivityType | null

    if (!activityType || activityType === ONLINE_ENCOUNTER) {
        return false
    }

    return true
}

const ThankYouScreen: React.FC = () => {
    useNavigateBackDisabled()
    const growthbook = useGrowthBook()
    const featuresReady = growthbook?.ready ?? false
    // useFeature re-renders when features finish loading — getFeatureValue does not
    const levelCompletionVariant = parseLevelCompletionVariant(
        useFeature(LEVEL_COMPLETION_FEATURE_FLAG).value ?? LevelCompletionVariant.OFF
    )
    const isTeacher = sessionStorage.getItem('role') === 'teacher'
    const activityType = sessionStorage.getItem('activityType') as ActivityType | null
    const coreCourseDashboardUrl = process.env.REACT_APP_CORE_COURSE_WEB_URL
        ? `${process.env.REACT_APP_CORE_COURSE_WEB_URL}${NEXT_STEP_URL}`
        : sessionStorage.getItem('callbackUrl') ?? ''

    const showStudentFeedback = shouldShowStudentFeedback()
    const isEncounterStudent = !isTeacher && activityType === ONLINE_ENCOUNTER

    // Conference is usually disconnected on /thanks; unit code is persisted at session join
    const unitNumber = parseUnitNumber(sessionStorage.getItem(UNIT_CODE_STORAGE_KEY) ?? undefined)

    // Wait for GrowthBook before enabling the check — avoids treating "not loaded yet" as OFF
    const shouldCheckContinueResult = isEncounterStudent &&
        featuresReady &&
        levelCompletionVariant !== LevelCompletionVariant.OFF
    const isContinueEncounterResult = useIsContinueEncounterResult(shouldCheckContinueResult)

    const goBackToDashboard = (): void => {
        window.location.href = coreCourseDashboardUrl
    }

    // CTA / auto-redirect both land on Learn next-step with the screen variant in the query string
    const continueFromLevelCompletion = (): void => {
        if (levelCompletionVariant === LevelCompletionVariant.OFF) {
            goBackToDashboard()
            return
        }
        const screenVariant = getLevelCompletionVariantLabel(levelCompletionVariant)
        const separator = coreCourseDashboardUrl.includes('?') ? '&' : '?'
        window.location.href =
            `${coreCourseDashboardUrl}${separator}${SCREEN_VARIANT_QUERY_PARAM}=${screenVariant}`
    }

    useEffect(() => {
        pushCameraUsageToGA()
    }, [])

    // Show student feedback flow for eligible students
    if (showStudentFeedback) {
        return <StudentThankYouScreen />
    }

    // Hold logo while GrowthBook/class result resolve — avoids thank-you flash for encounter students
    if (
        (isEncounterStudent && !featuresReady) ||
        (shouldCheckContinueResult && isContinueEncounterResult === null)
    ) {
        return <Logo />
    }

    // Encounter completion for encounter students with a "Continue" result
    if (
        shouldCheckContinueResult &&
        isContinueEncounterResult
    ) {
        return (
            <LevelCompletionScreen
                unitNumber={unitNumber}
                variant={levelCompletionVariant}
                onContinue={continueFromLevelCompletion}
            />
        )
    }

    // Show generic thank you for teachers and students not eligible for feedback
    return (
        <LogoContainer>
            <InnerWrapper>
                {!isTeacher && <img src={GroupIcon} />}
                <ThanksMessageWrapper>
                    <Title>Thanks for joining</Title>
                    <Message isTeacher={isTeacher}>
                        {
                            isTeacher
                                ? 'Congratulations! You just helped your students in their path to English excellence.'
                                : 'Did you enjoy your class? Get ready for the next one!'
                        }
                    </Message>
                </ThanksMessageWrapper>
                <RectangleButton
                    id='book-your-next-class-button'
                    color='error'
                    onClick={goBackToDashboard}
                    text={isTeacher ? 'Go to dashboard' : 'Book your next class'}
                />
            </InnerWrapper>
        </LogoContainer>
    )
}

export { ThankYouScreen, ThanksMessageWrapper, Title, Message }
